import * as React from "react";
import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from "@phosphor-icons/react";
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

function MonthPicker({
  currentDate,
  onDateChange,
}: {
  currentDate: Date;
  onDateChange: (date: Date) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const handleMonthSelect = (monthIndex: number) => {
    const newDate = new Date(currentYear, monthIndex, 1);
    onDateChange(newDate);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 px-2 font-medium text-sm hover:bg-accent"
        >
          {months[currentMonth]}
          <CaretDownIcon className="ml-1 h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-4 z-[9999]" align="center">
        <div>
          <h4 className="text-sm font-medium mb-2">Select Month</h4>
          <div className="grid grid-cols-3 gap-1">
            {months.map((month, index) => (
              <Button
                key={month}
                variant={index === currentMonth ? "default" : "ghost"}
                size="sm"
                className="h-8 text-xs"
                onClick={() => handleMonthSelect(index)}
              >
                {month}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function YearPicker({
  currentDate,
  onDateChange,
}: {
  currentDate: Date;
  onDateChange: (date: Date) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

  const handleYearSelect = (year: number) => {
    const newDate = new Date(year, currentMonth, 1);
    onDateChange(newDate);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 px-2 font-medium text-sm hover:bg-accent"
        >
          {currentYear}
          <CaretDownIcon className="ml-1 h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-4 z-[9999]" align="center">
        <div>
          <h4 className="text-sm font-medium mb-2">Select Year</h4>
          <div
            className="grid grid-cols-3 gap-1 max-h-48 overflow-y-auto"
            onWheel={(e) => {
              e.stopPropagation();
              const target = e.currentTarget;
              target.scrollTop += e.deltaY;
            }}
          >
            {years.map((year) => (
              <Button
                key={year}
                variant={year === currentYear ? "default" : "ghost"}
                size="sm"
                className="h-8 text-xs"
                onClick={() => handleYearSelect(year)}
              >
                {year}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function getLastDayOfMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function preserveDayInNewMonth(
  originalDate: Date,
  newYear: number,
  newMonth: number,
): Date {
  const originalDay = originalDate.getDate();
  const lastDayInNewMonth = getLastDayOfMonth(newYear, newMonth);
  const dayToUse = Math.min(originalDay, lastDayInNewMonth);
  return new Date(newYear, newMonth, dayToUse);
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  formatters,
  components,
  showTodayButton = true,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
  showTodayButton?: boolean;
}) {
  const defaultClassNames = getDefaultClassNames();
  const [currentMonth, setCurrentMonth] = React.useState<Date>(
    props.month || props.defaultMonth || new Date(),
  );

  // Derive selected from props
  const selectedDate =
    "selected" in props && props.selected instanceof Date
      ? props.selected
      : undefined;

  const handleMonthChange = (date: Date) => {
    // If we have a selected date, try to preserve the day in the new month
    const dateToUse = selectedDate
      ? preserveDayInNewMonth(selectedDate, date.getFullYear(), date.getMonth())
      : date;
    setCurrentMonth(dateToUse);
    props.onMonthChange?.(dateToUse);
  };

  const handleTodayClick = () => {
    const today = new Date();
    setCurrentMonth(today);
    props.onMonthChange?.(today);
    // If onSelect exists and we're in single mode, select today
    if (
      "onSelect" in props &&
      props.onSelect &&
      "mode" in props &&
      props.mode === "single"
    ) {
      const onSelectHandler = props.onSelect as (
        date: Date | undefined,
        selectedDate: Date | undefined,
        modifiers: Record<string, boolean>,
        e?: React.MouseEvent<HTMLButtonElement>,
      ) => void;
      onSelectHandler(today, today, {}, undefined);
    }
  };

  // Extract props we want to control ourselves
  const dayPickerProps = { ...props };
  const mode = "mode" in props ? props.mode : undefined;

  return (
    <div className="relative">
      {/* Custom Navigation Header */}
      <div className="flex items-center justify-between w-full px-3 pb-2">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const newDate = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() - 1,
                1,
              );
              handleMonthChange(newDate);
            }}
          >
            <CaretLeftIcon className="h-4 w-4" />
          </Button>
          <MonthPicker
            currentDate={currentMonth}
            onDateChange={handleMonthChange}
          />
          <YearPicker
            currentDate={currentMonth}
            onDateChange={handleMonthChange}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const newDate = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() + 1,
                1,
              );
              handleMonthChange(newDate);
            }}
          >
            <CaretRightIcon className="h-4 w-4" />
          </Button>
        </div>
        {showTodayButton && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleTodayClick();
            }}
          >
            Today
          </Button>
        )}
      </div>

      <DayPicker
        showOutsideDays={showOutsideDays}
        month={currentMonth}
        onMonthChange={handleMonthChange}
        {...(mode === "single"
          ? ({
              ...dayPickerProps,
              selected: selectedDate,
            } as React.ComponentProps<typeof DayPicker>)
          : dayPickerProps)}
        className={cn(
          "bg-background group/calendar p-3 pt-0 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
          String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
          String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
          className,
        )}
        captionLayout="label"
        formatters={{
          formatMonthDropdown: (date) =>
            date
              .toLocaleDateString("en-US", { weekday: "short" })
              .substring(0, 2),
          ...formatters,
        }}
        classNames={{
          root: cn("w-fit", defaultClassNames.root),
          months: cn(
            "flex gap-4 flex-col md:flex-row relative",
            defaultClassNames.months,
          ),
          month: cn("flex flex-col w-full gap-4", defaultClassNames.month),
          nav: cn(
            "flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between",
            showTodayButton ? "justify-between" : "justify-between",
            defaultClassNames.nav,
          ),
          button_previous: cn(
            "hidden", // Hide left navigation since we have enhanced picker
            defaultClassNames.button_previous,
          ),
          button_next: cn(
            "hidden", // Hide right navigation since we have enhanced picker
            defaultClassNames.button_next,
          ),
          month_caption: cn(
            "hidden", // Hide default caption since we have our own
            defaultClassNames.month_caption,
          ),
          dropdowns: cn(
            "w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5",
            defaultClassNames.dropdowns,
          ),
          dropdown_root: cn(
            "relative has-focus:border-ring border border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-md",
            defaultClassNames.dropdown_root,
          ),
          dropdown: cn(
            "absolute bg-popover inset-0 opacity-0",
            defaultClassNames.dropdown,
          ),
          caption_label: cn(
            "select-none font-medium",
            captionLayout === "label"
              ? "text-sm"
              : "rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5",
            defaultClassNames.caption_label,
          ),
          table: "w-full border-collapse",
          weekdays: cn(
            "flex w-full justify-between",
            defaultClassNames.weekdays,
          ),
          weekday: cn(
            "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] select-none text-center p-2 min-w-8 mx-1",
            defaultClassNames.weekday,
          ),
          week: cn("flex w-full mt-2", defaultClassNames.week),
          week_number_header: cn(
            "select-none w-(--cell-size)",
            defaultClassNames.week_number_header,
          ),
          week_number: cn(
            "text-[0.8rem] select-none text-muted-foreground",
            defaultClassNames.week_number,
          ),
          day: cn(
            "relative w-full h-full p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none",
            defaultClassNames.day,
          ),
          range_start: cn(
            "rounded-l-md bg-accent",
            defaultClassNames.range_start,
          ),
          range_middle: cn("rounded-none", defaultClassNames.range_middle),
          range_end: cn("rounded-r-md bg-accent", defaultClassNames.range_end),
          today: cn(
            "bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none",
            defaultClassNames.today,
          ),
          outside: cn(
            "text-muted-foreground aria-selected:text-muted-foreground",
            defaultClassNames.outside,
          ),
          disabled: cn(
            "text-muted-foreground opacity-50",
            defaultClassNames.disabled,
          ),
          hidden: cn("invisible", defaultClassNames.hidden),
          ...classNames,
        }}
        components={{
          Root: ({ className, rootRef, ...props }) => {
            return (
              <div
                data-slot="calendar"
                ref={rootRef}
                className={cn(className)}
                {...props}
              />
            );
          },
          Chevron: ({ className, orientation, ...props }) => {
            if (orientation === "left") {
              return (
                <CaretLeftIcon className={cn("size-4", className)} {...props} />
              );
            }

            if (orientation === "right") {
              return (
                <CaretRightIcon
                  className={cn("size-4", className)}
                  {...props}
                />
              );
            }

            return (
              <CaretDownIcon className={cn("size-4", className)} {...props} />
            );
          },
          DayButton: CalendarDayButton,
          WeekNumber: ({ children, ...props }) => {
            return (
              <td {...props}>
                <div className="flex size-(--cell-size) items-center justify-center text-center">
                  {children}
                </div>
              </td>
            );
          },
          ...components,
        }}
      />
    </div>
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
